import type { ComponentType } from 'react'
import { contentManifest } from '../content/content-manifest'

/** Frontmatter shape shared by content collections (see src/content/**.mdx). */
export type ContentMeta = {
  title: string
  /** optional concise SEO <title> (<=60 chars); the H1 keeps `title`. */
  seoTitle?: string
  /** optional hand-written meta description; falls back to summary/intro. */
  seoDescription?: string
  /** optional target keyphrase for the content-quality report (Yoast). */
  keyphrase?: string
  /** end-of-article FAQ: renders a visible Q&A block and FAQPage JSON-LD. */
  faq?: { q: string; a: string }[]
  /** old paths this post used to live at (e.g. after a slug rename). They are
   * prerendered with a canonical pointing at the new URL so the zero-404
   * contract holds; a public/_redirects rule 301s real traffic at the edge. */
  aliases?: string[]
  /** absolute URL of the canonical version when it lives off-site (syndicated
   * or co-published content). The page renders and stays linkable but its
   * <link rel="canonical"> points here and it is left out of the sitemap. */
  canonicalUrl?: string
  date?: string
  /** ISO date of the last substantive revision; shows as "Updated ..." in the
   * byline and feeds article:modified_time and JSON-LD dateModified. */
  updated?: string
  author?: string
  authorPhoto?: string
  image?: string
  /** square testimonial card image for the /customers grid (optional) */
  card?: string
  category?: string
  intro?: string
  /** opt-in: show this post's cover image in the listing (set only for real
   * photos; generated placeholder covers are dropped, not flagged). */
  listImage?: boolean
  /* ── research-paper fields (src/content/research/*.mdx) ──────────
   * PaperPage renders an arXiv-style header from these: the author list,
   * the one-line dataset provenance, and the abstract that precedes §1. */
  authors?: string[]
  /** one line naming the corpus, shown under the author list */
  dataset?: string
  /** abstract, rendered in the header block above the body */
  abstract?: string
  /** the companion blog post, linked from the paper header. Two scalars
   * rather than a nested object: the frontmatter parser reads scalars and
   * arrays only, and silently truncates inline YAML objects. */
  companionHref?: string
  companionLabel?: string

  /** changelog only */
  version?: string
  summary?: string
  /* ── customer-story fields (optional) ─────────────────────────────
   * Populate these in a customer .mdx frontmatter and CustomerStoryPage
   * renders the spec rail, results band, and pull quote from them. Leave
   * them out and the template still renders cleanly from title + intro +
   * body, so a brand-new story is just "drop in an .mdx". */
  company?: string
  /** e.g. "Hospitality tech" */
  industry?: string
  /** what they use LangWatch for, one line */
  useCase?: string
  /** public site, shown as the one outbound link in the rail */
  website?: string
  /** LangWatch features in use, rendered as pills */
  tools?: string[]
  /** headline outcomes for the results band */
  results?: { stat: string; label: string }[]
  /** pull quote and its attribution */
  quote?: string
  quoteAuthor?: string
  quoteRole?: string
}

export type Collection = 'blog' | 'changelog' | 'customers'

export type ContentEntry = {
  slug: string
  collection: Collection
  meta: ContentMeta
}

type ManifestItem = { slug: string; meta: ContentMeta }
const manifest = contentManifest as unknown as Record<string, ManifestItem[]>

// Frontmatter (above, from the generated manifest) is small and static, so
// indexes, the route table, and SEO stay synchronous. The compiled MDX bodies
// are heavy, so glob them WITHOUT `eager`: each becomes its own dynamic chunk,
// loaded only on its own route instead of bundled into the main chunk every
// page ships. `preloadContent` resolves and caches a body so the prerender and
// hydration can render it synchronously (see entry-server / entry-client).
type MdxModule = { default: ComponentType }
const bodyGlobs: Record<string, Record<string, () => Promise<MdxModule>>> = {
  blog: import.meta.glob('../content/blog/*.mdx') as Record<string, () => Promise<MdxModule>>,
  changelog: import.meta.glob('../content/changelog/*.mdx') as Record<string, () => Promise<MdxModule>>,
  customers: import.meta.glob('../content/customers/*.mdx') as Record<string, () => Promise<MdxModule>>,
  legal: import.meta.glob('../content/legal/*.mdx') as Record<string, () => Promise<MdxModule>>,
  research: import.meta.glob('../content/research/*.mdx') as Record<string, () => Promise<MdxModule>>,
}

const slugOf = (file: string): string => file.split('/').pop()!.replace(/\.mdx$/, '')

function loaderFor(dir: string, slug: string): (() => Promise<MdxModule>) | undefined {
  const glob = bodyGlobs[dir]
  if (!glob) return undefined
  const key = Object.keys(glob).find((f) => slugOf(f) === slug)
  return key ? glob[key] : undefined
}

const resolved = new Map<string, ComponentType>()
const cacheKey = (dir: string, slug: string): string => `${dir}/${slug}`

/** Load + cache one MDX body chunk. Await before render/hydrate so the body is
 * available synchronously (the prerender needs the full HTML for SEO). */
export async function preloadContent(dir: string, slug: string): Promise<void> {
  const key = cacheKey(dir, slug)
  if (resolved.has(key)) return
  const loader = loaderFor(dir, slug)
  if (!loader) return
  const mod = await loader()
  resolved.set(key, mod.default)
}

/** The cached MDX body component, available after `preloadContent` resolves. */
export function resolvedContent(dir: string, slug: string): ComponentType | undefined {
  return resolved.get(cacheKey(dir, slug))
}

function load(collection: Collection): ContentEntry[] {
  return (manifest[collection] ?? [])
    .map((item) => ({ slug: item.slug, collection, meta: item.meta }))
    // newest first; entries without a date sort last
    .sort((a, b) => (b.meta.date ?? '').localeCompare(a.meta.date ?? ''))
}

// Legal pages (privacy, terms) are standalone long-form prose, not a card
// collection, so they get a lightweight loader instead of the ContentEntry shape.
export function researchPapers(): { slug: string; meta: ContentMeta }[] {
  return (manifest.research ?? []).map((item) => ({ slug: item.slug, meta: item.meta }))
}

export function legalPages(): { slug: string; meta: ContentMeta }[] {
  return (manifest.legal ?? []).map((item) => ({ slug: item.slug, meta: item.meta }))
}

export const blogPosts = (): ContentEntry[] => load('blog')
export const changelogEntries = (): ContentEntry[] => load('changelog')
export const customerStories = (): ContentEntry[] => load('customers')
export const allContent = (): ContentEntry[] => [...blogPosts(), ...changelogEntries(), ...customerStories()]

/** Canonical path for a content entry, e.g. /blog/<slug>. */
export const contentPath = (e: Pick<ContentEntry, 'collection' | 'slug'>): string =>
  `/${e.collection}/${e.slug}`

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]

/** Format an ISO date as "June 2, 2026" from its calendar parts (timezone-safe for SSR). */
export function formatDate(iso?: string): string {
  if (!iso) return ''
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/)
  return m ? `${MONTHS[+m[2] - 1]} ${+m[3]}, ${m[1]}` : ''
}
